import { useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckSquare, Settings2, Square } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface M365Action {
  value: string
  labelKey: string
}

interface M365ActionPickerProps {
  actions: readonly M365Action[]
  selectedActions: string[]
  onToggleAction: (actionValue: string) => void
}

export function M365ActionPicker({
  actions,
  selectedActions,
  onToggleAction,
}: M365ActionPickerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.agentNode" })
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className="nodrag -m-0.5 size-6 p-0"
          size="icon"
          variant="ghost"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center">
                <Settings2 className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {t("m365Picker.configureActions")}
                <span className="ml-1 opacity-70">
                  ({selectedActions.length}/{actions.length})
                </span>
              </p>
            </TooltipContent>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="nodrag nowheel w-[280px] p-0"
        side="right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2">
          <div className="mb-2 text-xs font-medium">{t("m365Picker.selectActions")}</div>
          <div className="space-y-1">
            {actions.map((action) => {
              const isActive = selectedActions.includes(action.value)
              return (
                <button
                  key={action.value}
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                    isActive ? "bg-primary/10 ring-primary/30 ring-1" : "hover:bg-muted/50"
                  )}
                  onClick={() => onToggleAction(action.value)}
                >
                  <span
                    className={cn("shrink-0", isActive ? "text-primary" : "text-muted-foreground")}
                  >
                    {isActive ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </span>
                  <span className="text-sm">{t(action.labelKey)}</span>
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
